// src/components/PointsExplorer.js
import React, { useState, useEffect } from 'react';
import staticApi from '../services/staticApi';
import { saveShowData, loadShowData } from '../utils/showDataCache';
import { Select, SearchableDropdown, LoadingSpinner, ErrorAlert, InfoCard } from './shared/FormComponents';

const PointsExplorer = () => {
  // State management
  const [state, setState] = useState({
    years: [], selectedYear: new Date().getFullYear(), effectiveDate: '',
    states: [], selectedState: '', breeds: [], selectedBreed: '',
    dogPoints: [], bitchPoints: [], combinedPoints: [], divisionNumber: null,
    loading: true, error: null, breedSearchTerm: '', filteredBreeds: [], isBreedDropdownOpen: false
  });

  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  // Data fetching functions
  const fetchInitialData = async () => {
    try {
      updateState({ loading: true, error: null });
      const [yearsData, breedsData, statesByYearData] = await Promise.all([
        staticApi.getYears(), staticApi.getBreeds(), staticApi.getStatesByYear()
      ]);
      
      const currentYear = new Date().getFullYear();
      const availableYear = yearsData.find(y => y.year === currentYear) || yearsData[0];
      const yearStates = statesByYearData[availableYear.year.toString()] || [];

      // Try to load from cache
      const cached = loadShowData();
      let initialYear = availableYear.year;
      let initialState = yearStates[0] || '';
      let initialBreed = breedsData[0] || '';

      if (cached) {
        // Validate cached data against current dataset
        if (yearsData.find(y => y.year === cached.year)) {
          initialYear = cached.year;
        }
        const cachedYearStates = statesByYearData[initialYear.toString()] || [];
        if (cachedYearStates.includes(cached.state)) {
          initialState = cached.state;
        }
        if (breedsData.includes(cached.breed)) {
          initialBreed = cached.breed;
        }
      }

      updateState({
        years: yearsData, breeds: breedsData, filteredBreeds: breedsData,
        selectedYear: initialYear, selectedState: initialState,
        selectedBreed: initialBreed, states: statesByYearData[initialYear.toString()] || [],
        effectiveDate: yearsData.find(y => y.year === initialYear)?.effective_date
          ? new Date(yearsData.find(y => y.year === initialYear).effective_date).toLocaleDateString()
          : '',
        loading: false
      });
    } catch (err) {
      updateState({ error: `Error loading data: ${err.message}`, loading: false });
    }
  };

  const fetchPointsSchedule = async () => {
    const { selectedState, selectedBreed, selectedYear } = state;
    if (!selectedState || !selectedBreed || !selectedYear) {
      updateState({ dogPoints: [], bitchPoints: [] });
      return;
    }
    
    try {
      updateState({ loading: true });
      const scheduleData = await staticApi.getSchedule(selectedState, selectedBreed, selectedYear);
      updateState({
        dogPoints: scheduleData.dog_points, bitchPoints: scheduleData.bitch_points,
        divisionNumber: scheduleData.division_number, error: null, loading: false
      });
    } catch (err) {
      updateState({ error: `Error fetching schedule: ${err.message}`, dogPoints: [], bitchPoints: [], loading: false });
    }
  };

  // Effects
  useEffect(() => { 
    fetchInitialData(); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    const filtered = state.breeds.filter(breed => 
      breed.toLowerCase().includes(state.breedSearchTerm.toLowerCase())
    );
    updateState({ filteredBreeds: filtered });
  }, [state.breedSearchTerm, state.breeds]);

  useEffect(() => {
    if (state.selectedYear && state.years.length > 0) {
      staticApi.getStates(state.selectedYear).then(yearStates => {
        const yearInfo = state.years.find(y => y.year === state.selectedYear);
        updateState({
          states: yearStates,
          selectedState: yearStates.includes(state.selectedState) ? state.selectedState : yearStates[0] || '',
          effectiveDate: yearInfo ? new Date(yearInfo.effective_date).toLocaleDateString() : ''
        });
      });
    }
  }, [state.selectedYear, state.years, state.selectedState]);

  // Save selections to cache
  useEffect(() => {
    if (state.selectedYear && state.selectedState && state.selectedBreed) {
      saveShowData({
        year: state.selectedYear,
        state: state.selectedState,
        breed: state.selectedBreed
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedYear, state.selectedState, state.selectedBreed]);

  useEffect(() => {
    if (state.dogPoints.length > 0 && state.bitchPoints.length > 0) {
      const pointsSet = new Set([...state.dogPoints.map(p => p.points), ...state.bitchPoints.map(p => p.points)]);
      const combined = Array.from(pointsSet).sort((a, b) => a - b).map(points => ({
        points,
        dogCount: state.dogPoints.find(p => p.points === points)?.count || '-',
        bitchCount: state.bitchPoints.find(p => p.points === points)?.count || '-'
      }));
      updateState({ combinedPoints: combined });
    } else {
      updateState({ combinedPoints: [] });
    }
  }, [state.dogPoints, state.bitchPoints]);

  useEffect(() => { 
    fetchPointsSchedule(); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedState, state.selectedBreed, state.selectedYear]);

  // Event handlers
  const handleYearChange = (e) => updateState({ selectedYear: parseInt(e.target.value) });
  const handleStateChange = async (e) => {
    const newState = e.target.value;
    updateState({ selectedState: newState });
    if (newState) staticApi.preloadStateData(newState, state.selectedYear).catch(console.warn);
  };
  const handleBreedSelect = (breed) => updateState({ selectedBreed: breed, breedSearchTerm: breed, isBreedDropdownOpen: false });

  if (state.loading && !state.combinedPoints.length) {
    return <LoadingSpinner message="Loading Points Explorer..." />;
  }

  return (
    <div className="space-y-6">
      {/* Mobile-first Controls */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center">
          <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full mr-3"></div>
          Selection
        </h2>
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <Select
              label="Show Year"
              value={state.selectedYear}
              onChange={handleYearChange}
              options={state.years.map(y => ({ value: y.year, label: y.year }))}
            />
            <Select
              label="Show State"
              value={state.selectedState}
              onChange={handleStateChange}
              options={state.states}
              placeholder="Select a State"
            />
          </div>
          <SearchableDropdown
            label="Dog Breed"
            searchTerm={state.breedSearchTerm}
            onSearchChange={(e) => updateState({ breedSearchTerm: e.target.value, isBreedDropdownOpen: true })}
            options={state.filteredBreeds}
            onSelect={handleBreedSelect}
            isOpen={state.isBreedDropdownOpen}
            onToggle={(open) => updateState({ isBreedDropdownOpen: open })}
            placeholder="Search or select breed"
          />
        </div>
      </div>
      
      {/* Results */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {state.error && <ErrorAlert error={state.error} />}
        
        {state.combinedPoints.length > 0 ? (
          <>
            {/* Header - Mobile Responsive */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-100">
              <div className="text-center sm:text-left">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">{state.selectedBreed}</h3>
                <p className="text-sm sm:text-base text-gray-600 font-medium mt-1">
                  {state.selectedState} • Division {state.divisionNumber}
                </p>
                <div className="mt-2 sm:hidden">
                  <div className="inline-block bg-white px-3 py-1 rounded-full shadow-sm border border-gray-200">
                    <span className="text-xs font-semibold text-purple-600">Championship Points</span>
                  </div>
                </div>
              </div>
              <div className="hidden sm:block absolute top-4 right-6">
                <div className="bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200">
                  <span className="text-sm font-semibold text-purple-600">Championship Points</span>
                </div>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              {/* Mobile Card Layout for XS screens */}
              <div className="block sm:hidden space-y-3">
                {state.combinedPoints.map((row) => (
                  <div key={row.points} className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-center mb-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold rounded-full text-sm">
                        {row.points}
                      </span>
                      <span className="ml-2 text-sm font-medium text-gray-600">Points</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-lg font-bold text-gray-800">{row.dogCount}</div>
                        <div className="text-xs text-gray-600 font-medium">Dogs Required</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-800">{row.bitchCount}</div>
                        <div className="text-xs text-gray-600 font-medium">Bitches Required</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table Layout */}
              <div className="hidden sm:block overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-purple-500 to-indigo-500">
                      <tr>
                        {['Points', 'Dogs Required', 'Bitches Required'].map(header => (
                          <th key={header} className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {state.combinedPoints.map((row, i) => (
                        <tr key={row.points} className={`hover:bg-purple-50 transition-colors duration-150 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold rounded-full text-sm">
                              {row.points}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center text-gray-800 font-semibold text-lg">{row.dogCount}</td>
                          <td className="px-6 py-4 text-center text-gray-800 font-semibold text-lg">{row.bitchCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="mt-6">
                <InfoCard title="Championship Requirements">
                  <p className="text-sm sm:text-base">
                    These values represent the number of dogs or bitches required to earn the corresponding championship points for <strong>{state.selectedBreed}</strong> in <strong>{state.selectedState}</strong> for <strong>{state.selectedYear}</strong>.
                  </p>
                </InfoCard>
              </div>
            </div>
          </>
        ) : !state.error && (
          <div className="p-8 sm:p-12 text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-lg sm:text-xl text-gray-500 font-medium">Select a state and breed to view the points schedule</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsExplorer;